import { Component, ReactNode, ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary component that catches and displays React errors
 * Provides a user-friendly error UI with retry functionality
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console (in production, this would go to monitoring service)
    console.error('ErrorBoundary caught an error:', error)
    console.error('Error component stack:', errorInfo.componentStack)

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null
    })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset)
      }

      // Default error UI
      return <DefaultErrorFallback error={this.state.error} onReset={this.handleReset} />
    }

    return this.props.children
  }
}

interface DefaultErrorFallbackProps {
  error: Error
  onReset: () => void
}

function DefaultErrorFallback({ error, onReset }: DefaultErrorFallbackProps) {
  // Detect theme from document class (since we can't use hooks in class components)
  const isDark = typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')

  return (
    <div
      className={`min-h-[400px] flex items-center justify-center p-6 ${
        isDark ? 'bg-slate-950/50' : 'bg-slate-50/50'
      }`}
    >
      <div
        className={`max-w-md w-full p-6 rounded-xl shadow-lg text-center ${
          isDark
            ? 'bg-slate-900 border border-white/10'
            : 'bg-white border border-slate-200'
        }`}
      >
        {/* Error Icon */}
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
          isDark ? 'bg-red-500/20' : 'bg-red-100'
        }`}>
          <AlertTriangle className={`w-8 h-8 ${
            isDark ? 'text-red-400' : 'text-red-600'
          }`} />
        </div>

        {/* Error Message */}
        <h2 className={`text-xl font-semibold mb-2 ${
          isDark ? 'text-white' : 'text-slate-900'
        }`}>
          Something went wrong
        </h2>
        <p className={`text-sm mb-4 ${
          isDark ? 'text-white/60' : 'text-slate-600'
        }`}>
          We encountered an unexpected error. Please try again.
        </p>

        {/* Error Details (collapsed by default in production) */}
        {process.env.NODE_ENV === 'development' && (
          <details className={`mb-4 text-left ${
            isDark ? 'text-white/50' : 'text-slate-500'
          }`}>
            <summary className="cursor-pointer text-xs font-medium mb-2">
              Error details
            </summary>
            <pre className={`text-xs p-3 rounded overflow-auto max-h-32 ${
              isDark ? 'bg-black/30' : 'bg-slate-100'
            }`}>
              {error.message}
            </pre>
          </details>
        )}

        {/* Retry Button */}
        <button
          onClick={onReset}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium
                    transition-colors ${
            isDark
              ? 'bg-purple-600 hover:bg-purple-700 text-white'
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </button>
      </div>
    </div>
  )
}

/**
 * Hook-based error boundary wrapper for functional components
 * Usage: <ErrorBoundaryWrapper>{children}</ErrorBoundaryWrapper>
 */
export function ErrorBoundaryWrapper({ children }: { children: ReactNode }) {
  return <ErrorBoundary>{children}</ErrorBoundary>
}
